import {useTranslation} from "react-i18next";
import {useModal} from "../../../core/context/ModalContext";
import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import ApiService from "../../../core/service/api-service";
import {useEffect, useRef, useState} from "react";
import {dateFormat} from "../../../core/utils/date-utils";
import {Button, Checkbox, Dropdown, Menu, message, Tooltip, Typography, Tag} from "antd";
import {v4} from "uuid";
import {Pages} from "../../../AppRouter";
import {DownOutlined} from "@ant-design/icons";
import Flex from "../../../core/components/Flex";
import DataGrid, {getGridBodyHeight} from "../../../core/components/data-grid/DataGrid";
import {isRowEvent} from "../../../core/components/data-grid/rowEvent";
import ReportRowMenu from "./ReportRowMenu";
import RequestSourceIcon from "../../request/RequestSourceIcon";
import ReportGridTools from "./ReportGridTools";
import {toReportGridQuery} from "./report-filter-query";

import './row-styles.css'
import ReportPreview from "../ReportPreview";
import {PERMISSIONS, useUser} from "../../../core/context/UserContext";
import {t} from "i18next";
import Colors from "../../../core/style/Colors";
import {useSignalR} from "../../../core/context/SignalRContext";
import useFocusListener from "../../../core/components/FocusListener";
import {useApp} from "../../../core/context/AppContext";
import ReportModal from "../../../pages/report/ReportModal";
import {studyTaskListKey, studyTaskTypeKey} from "../../../enums/StudyTaskTypeEnum";

export default function ReportGrid() {
    const {t} = useTranslation();

    const {hasPermission} = useUser();
    const {onUpdateStudyGrid} = useSignalR()
    const {setDataGridProps, queryModel, setData, setPaginationResult, tableId, setRowColor, refreshDataGrid, loadCount, setLoading} = useDataGrid()

    const isFocus = useFocusListener({elementId: tableId})
    const {addKeyEvent, removeKeyEvent, pressedKey} = useApp()
    const {openModal, closeModal} = useModal()

    const selectedReport = useRef(null)
    // Seçim değişince grid'i yeniden render edip rowClassName'i (selected-row) tazelemek için.
    const [, forceRender] = useState(0)

    const openReportPreviewModal = (studyId, requestId, reportId, studyTasks) => {
        openModal(
            t('Report Preview'),
            <ReportPreview
                studyId={studyId}
                requestId={requestId}
                reportId={reportId}
                studyTasks={studyTasks}
                onDataChanged={() => {
                    refreshDataGrid();
                }}
            />
        );
    }

    useEffect(() => {
        addKeyEvent([
            {keyCode: 'KeyR', modifier: null}
        ])
    }, []);

    useEffect(() => {
        console.log(pressedKey)
        if(!selectedReport.current)
            return

        if(!isFocus)
            return;

        const element = document.activeElement;
        if(element.tagName === 'INPUT')
            return

        if(pressedKey?.key?.code === 'KeyR' && !pressedKey?.modifier?.code) {
            openReqReportPage()
        }
    }, [pressedKey]);

    const openReqReportPage = () => {
        if(!hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT))
            return

        openModal(t('Report'), (
            <ReportModal
                studyId={selectedReport.current.studyId}
                requestId={selectedReport.current.requestId}
                onRefreshGrid={refreshDataGrid}
            />
        ), null, window.innerWidth*0.98, 20)
    }

    /**
     * background: SignalR tazelemesi — kullanıcı istemedi, gösterge çıkmaz (bkz. StudyGrid).
     * Kullanıcının başlattığı yüklemede gösterge tablonun içinde; sayfa kararmaz.
     */
    const initDataGrid = async ({ background = false } = {}) => {
        if (!background)
            setLoading(true)

        // Tarih normalizasyonu ortak: Excel de aynı fonksiyondan geçer (bkz. report-filter-query).
        const _qm = toReportGridQuery(queryModel)

        try {
            // silent: küresel gösterge susar; geri bildirim tablonun kendi loading'inde.
            const response = await ApiService.Report.GetDataGrid(_qm, { silent: true });
            setData(response.data)
            setPaginationResult(response.pagination)

            // Sayaç ayrı istek, sayfa çizildikten SONRA (bkz. StudyGrid).
            loadCount((exact) => ApiService.Report.GetDataGridCount(_qm, exact), JSON.stringify(_qm.filter))
        }
        finally {
            if (!background)
                setLoading(false)
        }
    }

    const setHbysTransfer = async (row) => {
        const response = await ApiService.Report.SetHbysTransfer(row.studyId, row.requestId, row.id, !row.isHbysTransfer)
        if (response.success) {
            await refreshDataGrid()
        }
    }

    useEffect(() => {
        if(hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER) && onUpdateStudyGrid?.key) {
            // SignalR tetikledi: kullanıcı istemedi → sessiz tazeleme (gösterge yok).
            initDataGrid({background: true})
        }
    }, [onUpdateStudyGrid])

    useEffect(() => {
        if(window.reportGridKey !== queryModel.updateKey) {
            window.reportGridKey = queryModel.updateKey
            initDataGrid()
        }
    }, [queryModel.updateKey])

    useEffect(() => {
        let columns = [
            {
                title: 'Id',
                key: 'id',
                columnName: 'id',
                width: 80,
                sorter: true,
                render: (text, record) => (
                    <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                        <ReportRowMenu row={record}/>
                        <RequestSourceIcon isHL7={record.isHL7}/>
                    </div>
                )
            },
            {
                title: t('Report Date'),
                dataIndex: 'reportDate',
                key: 'reportDate',
                columnName: 'createdDate',
                sorter: true,
                width: 115,
                render: (text, record) => {
                    return dateFormat(text)
                }
            },
            {
                title: t('Written By User'),
                dataIndex: 'writtenByUser',
                key: 'writtenByUser',
                columnName: 'wroteUser',
                width: 60,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 60}}>
                            {text}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Patient Id'),
                dataIndex: 'dicomPatientId',
                key: 'dicomPatientId',
                columnName: 'study.patient.dicomPatientId',
                sorter: true,
                width: 100,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 100}}>
                            {text}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Patient'),
                dataIndex: 'patientName',
                key: 'patientName',
                columnName: 'request.study.patient.name',
                sorter: true,
                width: 130,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 130}}>
                            {text}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Institution'),
                dataIndex: 'institution',
                key: 'institution',
                columnName: 'institution',
                sorter: true,
                width: 80,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: record.institution || '-'}} style={{width: 80}}>
                            {record.institution || '-'}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Hospital'),
                dataIndex: 'hospitalName',
                key: 'hospitalName',
                columnName: 'study.pacsSystem.hospital.name',
                sorter: true,
                width: 75,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 75}}>
                            {text}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Request Name'),
                dataIndex: 'requestName',
                key: 'requestName',
                width: 175,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 175}}>
                            {text}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Modality'),
                dataIndex: 'modality',
                key: 'modality',
                width: 65,
                columnName: 'study.modality.name',
                sorter: true,
            },
            {
                title: t('Study Date'),
                dataIndex: 'studyDate',
                key: 'studyDate',
                columnName: 'study.studyDateTime',
                sorter: true,
                width: 115,
                render: (text, record) => text ? dateFormat(text) : '-'
            },
            {
                title: t('Study Description'),
                dataIndex: 'studyDescription',
                key: 'studyDescription',
                width: 200,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 200}}>
                            {text}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Read Date'),
                dataIndex: 'readDate',
                key: 'readDate',
                columnName: 'readDate',
                width: 115,
                render: (text, record) => {
                    return text ? dateFormat(text) : '-'
                }
            },
            {
                title: t('Read By User'),
                dataIndex: 'readByUser',
                key: 'readByUser',
                columnName: 'readByUser',
                width: 60,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 60}}>
                            {text ? text : '-'}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Assigned Doctor'),
                dataIndex: 'assignedDoctorName',
                key: 'assignedDoctorName',
                columnName: 'assignedDoctorName',
                width: 60,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 60}}>
                            {text ? text : '-'}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Assigned Reporter'),
                dataIndex: 'assignedReporterName',
                key: 'assignedReporterName',
                columnName: 'assignedReporterName',
                width: 60,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 60}}>
                            {text ? text : '-'}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Confirm Date'),
                dataIndex: 'confirmDate',
                key: 'confirmDate',
                columnName: 'confirmDate',
                width: 120,
                render: (text, record) => {
                    if(!record.studyTasks || record.studyTasks.length === 0)
                        return text ? dateFormat(text) : '-'

                    return (
                        <Tooltip
                            title={
                                <div>
                                    {record.studyTasks.map((task, idx) => (
                                        <div key={idx} style={{marginBottom: 4}}>
                                            <strong>{t(studyTaskTypeKey(task.taskType))}</strong>
                                            {task.description && <div style={{fontSize: 11, color: '#ccc'}}>{task.description}</div>}
                                        </div>
                                    ))}
                                </div>
                            }
                        >
                            <Tag color="orange">{t(studyTaskListKey(record.studyTasks))}</Tag>
                        </Tooltip>
                    );


                }
            },
            {
                title: t('Confirmed By User'),
                dataIndex: 'confirmedByUser',
                key: 'confirmedByUser',
                columnName: 'confirmedByUser',
                width: 60,
                render: (text, record) => {
                    return (
                        <Typography.Text ellipsis={{tooltip: text}} style={{width: 60}}>
                            {text ? text : '-'}
                        </Typography.Text>
                    )
                }
            },
            {
                title: t('Sign'),
                dataIndex: 'isSigned',
                key: 'isSigned',
                columnName: 'isSigned',
                width: 40,
                render: (text, record) => {
                    return text ? '✔' : '-'
                }
            },
            {
                title: 'Hbys',
                key: 'action',
                width: 40,
                render: (text, record) => {
                    if(!record.isConfirm)
                        return '-'

                    if (!hasPermission(PERMISSIONS.REPORT_HBYS_TRANSFER)) {
                        return <Typography.Text>{record.isHbysTransfer ? '✓' : '𐄂'}</Typography.Text>
                    }

                    return (
                        <Checkbox
                            checked={record.isHbysTransfer}
                            onChange={async (e) => {
                                e.preventDefault();
                                await setHbysTransfer(record)
                            }}/>
                    )
                }
            },
        ];

        if(!hasPermission(PERMISSIONS.STUDY_GRID_USER_INFO)) {
            columns = columns.filter(x => x.dataIndex !== 'readByUser')
            columns = columns.filter(x => x.dataIndex !== 'assignedDoctorName')
            columns = columns.filter(x => x.dataIndex !== 'assignedReporterName')
            columns = columns.filter(x => x.dataIndex !== 'assignedReporterName')
            columns = columns.filter(x => x.dataIndex !== 'confirmedByUser')
            columns = columns.filter(x => x.dataIndex !== 'writtenByUser')
        }

        if(!hasPermission(PERMISSIONS.VIEW_STUDY_INSTITUTION)) {
            columns = columns.filter(x => x.dataIndex !== 'institution')
        }

        setDataGridProps({
            rowKey: 'id',
            columns: columns,
            enableRowSelection: true,
            virtual: true,
            antdTableProps: {
                // +40 for the selection column; scroll.y gives a fixed body height
                // so the rows virtualize. Fixed layout keeps header/body aligned.
                scroll: {
                    x: 40 + columns.map(x => x.width || 0).reduce((a, b) => a + b, 0),
                    y: getGridBodyHeight()
                },
                tableLayout: 'fixed'
            },
            rowClassName: (record, index) => {
                let className = 'normal-row';

                if (record.isRequestDeleted)
                    className = 'deleted-row';
                else if (record.isConfirm)
                    className = 'confirmed-report-row';
                else if (!record.isConfirm)
                    className = 'not-confirmed-report-row'
                else
                    className = 'normal-row'

                if (selectedReport.current?.id === record.id)
                    className += ' selected-row';

                return className;
            },
            onRow: (record) => {
                return {
                    // isRowEvent: satır menüsünden açılan modal/dropdown gövdeye portal edilse de
                    // React olayları satıra ulaşıyor; olay satırdan gelmiyorsa yok say (bkz. rowEvent.js).
                    onClick: (event) => {
                        if (!isRowEvent(event))
                            return;

                        if(record?.id === selectedReport.current?.id) {
                            selectedReport.current = null
                        }
                        else {
                            selectedReport.current = record
                        }
                        forceRender(x => x + 1);
                    },
                    onDoubleClick: (event) => {
                        if (!isRowEvent(event))
                            return;

                        selectedReport.current = record
                        forceRender(x => x + 1);
                        hasPermission(PERMISSIONS.REPORT_LIST) && openReportPreviewModal(record.studyId, record.requestId, record.id, record.studyTasks)
                    }
                }
            }
        })
    }, [])

    return (
        <>

            <DataGrid 
                name={'report'} 
                title={() => <ReportGridTools/>}
                mobileCardTitle={(record) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{record.patientName}</span>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <span style={{ 
                                    backgroundColor: record.isConfirm ? '#52c41a' : '#faad14', 
                                    color: '#fff', 
                                    padding: '2px 6px', 
                                    borderRadius: 4, 
                                    fontSize: 11,
                                    fontWeight: 500
                                }}>
                                    {record.isConfirm ? t('Confirmed') : t('Not Confirmed')}
                                </span>
                                <span style={{ 
                                    backgroundColor: '#1890ff', 
                                    color: '#fff', 
                                    padding: '2px 6px', 
                                    borderRadius: 4, 
                                    fontSize: 11,
                                    fontWeight: 500
                                }}>
                                    {record.modality}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-2)', alignItems: 'center' }}>
                            <ReportRowMenu row={record} />
                            <RequestSourceIcon isHL7={record.isHL7} />
                            <span>#{record.id}</span>
                            <span>{t('Patient ID')}: {record.dicomPatientId}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                            <Typography.Text ellipsis style={{ maxWidth: '100%' }}>
                                {record.requestName}
                            </Typography.Text>
                        </div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-3)' }}>
                            <span>{t('Report Date')}: {dateFormat(record.reportDate)}</span>
                            {record.confirmDate && (
                                <span>{t('Confirm Date')}: {dateFormat(record.confirmDate)}</span>
                            )}
                        </div>
                    </div>
                )}
            />
        </>
    )
}
